import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {Chip, IconButton} from '@mui/material';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {setSnackbar} from '../../../store/reducers/globals';
import API from '../../../app/api';
import clsx from 'clsx';
import {INIT_FILTER_STATE, schedulerFacility, setFacility, setFilters} from '../../../store/reducers/appointments';
import { setAdmissionSelectedHospital } from '../../../store/reducers/admission';

const useStyles = makeStyles((theme) => ({
    menuItem: {
        fontSize: 'small',
        width: '100%',
        minWidth: 100,
        display: 'flex',
        justifyContent: 'flex-start',
    },
    hospital: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontWeight: 'bold',
        marginRight: 5,
        cursor: 'pointer',
    },
    searchText: {
        height: 18,
    },
    textField: {
        backgroundColor: theme.palette.mode === 'light' ? '#e3e3e3' : '#434343',
    },
    iconHover: {
        borderRadius: 6,
        '&:hover': {
            backgroundColor: theme.palette.background.dialogBorder,
        },
    },
    error : {
        color: theme.palette.error.main,
    },
}));

const HospitalSelection = ({hospitalId, allowHospitalClear}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const facility = useSelector(schedulerFacility);

    // Data
    const [hospitals, setHospitals] = useState([]);
    const [selectedTrust, setSelectedTrust] = useState(null);
    const [filterText, setFilterText] = React.useState('');

    // Menu state
    const [menuEl, setMenuEl] = useState(null);
    const open = Boolean(menuEl);

    // Download TRUD and Hospitals dict
    useEffect(() => {
        const controller = new AbortController();
        API.getHospitals('forMCT eq true', controller.signal)
            .then(data => {
                if (!_.isEmpty(data?.data?.__data)) {
                    setHospitals(data.data.__data);
                }
            }).catch(err => {
            dispatch(setSnackbar({
                type: 'error',
                message: 'There was a problem downloading the hospitals list.',
            }));
            console.error(err);
        });

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (hospitalId && !_.isEmpty(hospitals) && selectedTrust === null) {
            const hospitalName = _.find(hospitals, {__id: hospitalId});
            setSelectedTrust(hospitalName);
        } else if ((facility && selectedTrust === null) && !_.isEmpty(hospitals)) {
            const hospitalName = _.find(hospitals, {__id: facility});
            setSelectedTrust(hospitalName);
        }
    }, [hospitalId, hospitals, facility]);

    useEffect(() => {
        if (selectedTrust) {
            dispatch(setFacility(selectedTrust?.__id));
            dispatch(setAdmissionSelectedHospital(selectedTrust));
        }
    }, [selectedTrust]);

    const handleCloseMenu = () => {
        setMenuEl(null);
        setFilterText('');
    };

    const handleFilterChange = (event) => {
        event.preventDefault();
        if (!event.target.value && event.target.value.length === 0) {
            setFilterText('');
        } else {
            setFilterText(event.target.value);
        }
    };

    const filteredList = (array, fieldSearch) => {
        return _.filter(array, t => _.toLower(t[fieldSearch]).includes(filterText?.toLowerCase()));
    };

    const handleSelectTrust = (obj) => {
        setFilterText('');
        setSelectedTrust(obj);
        dispatch(setFacility(obj?.__id));
        dispatch(setFilters(INIT_FILTER_STATE));
        handleCloseMenu();
    };

    if (_.isEmpty(hospitals)) return null;

    return (
        <>
            <div
                className={classes.hospital}
                onClick={e => {
                    if (!open) {
                        setMenuEl(e.target);
                    }
                }}
            >
                <span className={clsx(!selectedTrust && classes.error)}>
                    {_.startCase(selectedTrust?.description?.toLowerCase() || 'Hospital not selected')}
                </span>

                <IconButton
                    size="small"
                    color="default"
                    className={classes.iconHover}
                >
                    <KeyboardArrowDownIcon fontSize="small"/>
                </IconButton>
            </div>

            {allowHospitalClear && selectedTrust && hospitals?.length > 1 && (
                <IconButton
                    size="small"
                    color="default"
                    className={classes.iconHover}
                    onClick={() => handleSelectTrust(undefined)}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            )}

            {open &&
                <Menu
                    anchorEl={menuEl}
                    id="ward-menu-map"
                    open={open}
                    onClose={handleCloseMenu}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            p: 1,
                            borderRadius: '10px',
                        },
                    }}
                    BackdropProps={{
                        invisible: true,
                    }}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                >
                    <AgyleTextField
                        inputPropsClasses={classes.searchText}
                        textFieldClass={classes.textField}
                        size="small"
                        fullWidth
                        placeholder={t(`Search hospital`)}
                        variant="contained"
                        onChange={handleFilterChange}
                        value={filterText}
                        noBorder={false}
                        startAdornment={<SearchIcon style={{marginRight: 8}}/>}
                        shrink
                        smallerPlaceholder
                        style={{marginBottom: 5}}
                    />
                    {filteredList(hospitals, 'description')
                        .map(obj => (
                            <MenuItem
                                key={obj?.__id}
                                className={classes.menuItem}
                                onClick={() => handleSelectTrust(obj)}
                                selected={obj?.__id === selectedTrust?.__id}
                            >
                                <Chip
                                    label={obj?.__id || 'UNK'}
                                    size="small"
                                    sx={{mr: 1, minWidth: 65}}
                                />
                                <span>
                                {_.startCase(obj?.description?.toLowerCase() || '')}
                            </span>
                            </MenuItem>
                        ))}
                </Menu>
            }
        </>
    );
};

export default HospitalSelection;